import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse, HttpEvent,
  HttpHeaders,
  HttpParams,
  HttpResponse
} from '@angular/common/http';
import { Observable, Subject, takeUntil, throwError } from 'rxjs';
import { catchError, finalize, retry } from 'rxjs/operators';
import {
  createNetworkRetryStrategy,
  DEFAULT_NETWORK_RETRY_CONFIG,
  NetworkRetryConfig, SHORTEN_NETWORK_RETRY_CONFIG
} from '../utils/network-retry-strategy';

interface HttpBaseRequestCustomOptions {
  retryConfig?: NetworkRetryConfig;
  requestTrackId?: string;
}

interface HttpBaseRequestOptions extends HttpBaseRequestCustomOptions {
  headers?: HttpHeaders | Record<string, string | string[]>;
  params?: HttpParams | Record<string, string | string[]>;
  reportProgress?: boolean;
  withCredentials?: boolean;
  context?: HttpContext;
  keepalive?: boolean;
  transferCache?: {includeHeaders?: string[]} | boolean;
  observe?: 'body' | 'events' | 'response';
  responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
}

interface HttpBlobResponseRequestOptions extends HttpBaseRequestOptions {
  observe: 'response';
  responseType: 'blob';
}

interface HttpJsonResponseRequestOptions extends HttpBaseRequestOptions {
  observe: 'response';
  responseType?: 'json';
}

interface HttpJsonBodyRequestOptions extends HttpBaseRequestOptions {
  observe?: 'body';
  responseType?: 'json';
}

@Injectable({
  providedIn: 'root'
})
export class HttpRetryableService {

  private readonly abortRequestsByTrackId: Map<string, Subject<void>> = new Map<string, Subject<void>>();

  constructor(
    private readonly http: HttpClient
  ) {
  }

  get(
    url: string,
    options: HttpBlobResponseRequestOptions,
  ): Observable<HttpResponse<Blob>>;

  get<T>(
    url: string,
    options?: HttpJsonBodyRequestOptions,
  ): Observable<T>;

  get<T>(
    url: string,
    options?: HttpJsonResponseRequestOptions,
  ): Observable<HttpResponse<T>>;

  get(
    url: string,
    options: HttpBaseRequestOptions = {}
  ): Observable<unknown> {
    return this.createHttpRequest(
      this.http.get<never>(url, options as never),
      SHORTEN_NETWORK_RETRY_CONFIG,
      options
    );
  }

  post<T>(url: string, body: unknown, options?: HttpJsonBodyRequestOptions): Observable<T>;

  post<T>(url: string, body: unknown, options?: HttpJsonResponseRequestOptions): Observable<HttpResponse<T>>;

  post<T>(url: string, body: unknown, options?: HttpBaseRequestOptions): Observable<T | HttpEvent<T> | HttpResponse<T>> {
    return this.createHttpRequest(
      this.http.post<T>(url, body, options as never),
      DEFAULT_NETWORK_RETRY_CONFIG,
      options
    );
  }

  put<T>(url: string, body: unknown, options?: HttpBaseRequestOptions): Observable<T | HttpEvent<T> | HttpResponse<T>> {
    return this.createHttpRequest(
      this.http.put<T>(url, body, options as never),
      DEFAULT_NETWORK_RETRY_CONFIG,
      options
    );
  }

  delete<T>(url: string, options?: HttpJsonBodyRequestOptions): Observable<T>;

  delete<T>(url: string, options?: HttpBaseRequestOptions): Observable<T | HttpEvent<T> | HttpResponse<T>> {
    return this.createHttpRequest(
      this.http.delete<T>(url, options as never),
      DEFAULT_NETWORK_RETRY_CONFIG,
      options
    );
  }

  private createHttpRequest<T>(
    from: Observable<T>,
    defaultRetryConfig: NetworkRetryConfig,
    options?: HttpBaseRequestOptions
  ): Observable<T> {
    let httpRequest$ = from.pipe(
      retry(createNetworkRetryStrategy(options?.retryConfig || defaultRetryConfig))
    );
    const trackId = options?.requestTrackId;
    if (trackId) {
      this.abortRequest(trackId);
      const aborted$: Subject<void> = this.assignAbortRequest(trackId);
      httpRequest$ = httpRequest$.pipe(takeUntil(aborted$));
    }
    return httpRequest$.pipe(
      catchError(this.handleError),
      finalize(() => {
        if (trackId) {
          this.abortRequest(trackId);
        }
      })
    );
  }

  private assignAbortRequest(trackId: string): Subject<void> {
    const request$ = new Subject<void>();
    this.abortRequestsByTrackId.set(trackId, request$);
    return request$;
  }

  private abortRequest(trackId: string): void {
    const abortRequest$ = this.abortRequestsByTrackId.get(trackId);
    if (abortRequest$) {
      this.abortRequestsByTrackId.delete(trackId);
      abortRequest$.next();
      abortRequest$.complete();
    }
  }

  private handleError(error: HttpErrorResponse) {
    return throwError(() => error);
  }
}
